import { suiteApps } from "@/components/layout/navConfig";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/**
 * The Suite manifest — the single source of truth for the Help Genie app
 * family, rendered by the GenieSwitcher in every app.
 *
 * Served as /apps.json so satellite apps (Mail, Sync, Outbound) can fetch the
 * same list cross-origin and stay in step without a redeploy. This module
 * bakes a fallback derived from `navConfig.suiteApps` so the switcher renders
 * even when the fetch fails (offline, first paint, satellite CORS hiccup).
 */
export interface SuiteManifestApp {
  id: string;
  productWord: string;
  /** Genie Icon Set name — what every app renders. */
  genieIcon?: GenieIconName;
  /** Legacy PNG path kept for satellites that still render images; drop once all apps use `genieIcon`. */
  iconUrl: string;
  color: string;
  description: string;
  /** The hub app (Voice). Exactly one entry should carry this. */
  home?: boolean;
  beta?: boolean;
  /** Internal route fallback when no externalUrl exists */
  path?: string;
  /** Canonical external app URL — launches via SSO */
  externalUrl?: string;
  /** Path on the external app that accepts SSO token query params */
  ssoEntryPath?: string;
}

export const HOME_APP: SuiteManifestApp = {
  id: "voice",
  productWord: "Voice",
  genieIcon: "microphone",
  iconUrl: "/icons/microphone-waves.png",
  color: "#7F84EA",
  description: "Build and run your Genies — calls, chat, knowledge.",
  home: true,
  // Satellites render this as their "Home" row. Inside Voice the row is the
  // current app (never launched), so this URL is only followed from satellites.
  externalUrl: "https://helpgenie.ai/dashboard",
};

const LEGACY_ICON_URLS: Record<string, string> = {
  mail: "/icons/email-envelope.png",
  sync: "/icons/refresh-sync.png",
  outbound: "/icons/phone-call.png",
};

export const bakedSuiteManifest: SuiteManifestApp[] = [
  HOME_APP,
  ...suiteApps.map((app) => ({
    id: app.productWord.toLowerCase(),
    productWord: app.productWord,
    genieIcon: app.genieIcon,
    iconUrl: LEGACY_ICON_URLS[app.productWord.toLowerCase()],
    color: app.color,
    description: app.description,
    path: app.path,
    externalUrl: app.externalUrl,
    ssoEntryPath: app.ssoEntryPath,
  })),
];

const isValidApp = (app: unknown): app is SuiteManifestApp => {
  if (typeof app !== "object" || app === null) return false;
  const a = app as Record<string, unknown>;
  return (
    typeof a.id === "string" &&
    typeof a.productWord === "string" &&
    typeof a.iconUrl === "string" &&
    typeof a.color === "string" &&
    typeof a.description === "string"
  );
};

/** Fetch the manifest; falls back to the baked list rather than throwing. */
export const fetchSuiteManifest = async (): Promise<SuiteManifestApp[]> => {
  try {
    const res = await fetch("/apps.json", { headers: { accept: "application/json" } });
    if (!res.ok) return bakedSuiteManifest;
    const data: unknown = await res.json();
    const apps = (data as { apps?: unknown[] })?.apps;
    const valid = Array.isArray(apps) ? apps.filter(isValidApp) : [];
    return valid.length > 0 ? valid : bakedSuiteManifest;
  } catch {
    return bakedSuiteManifest;
  }
};
